import { Suspense, lazy, useMemo } from 'react';

import type { StoreDailyMetricsResponse } from 'src/api/models';

import type { ApexOptions } from 'apexcharts';

const ReactApexChart = lazy(() => import('react-apexcharts'));

const chartOptions: ApexOptions = {
  stroke: { curve: 'smooth' },
  chart: {
    toolbar: { show: false },
    animations: { enabled: true },
    selection: { enabled: false },
    zoom: { enabled: false },
  },
  fill: {
    colors: ['#1A56DB', '#FDBA8C'],
    type: 'gradient',
    gradient: {
      opacityFrom: 0.45,
      opacityTo: 0,
    },
  },
  grid: {
    strokeDashArray: 1,
    borderColor: '#F3F4F6',
  },
  dataLabels: {
    enabled: false,
  },
  legend: {
    fontWeight: 700,
    fontSize: '14px',
    labels: {
      colors: '#6B7280',
    },
    itemMargin: {
      horizontal: 10,
    },
  },
  xaxis: {
    type: 'datetime',
    labels: {
      style: {
        colors: '#6B7280',
        fontSize: '14px',
        fontWeight: 700,
      },
    },
  },
  yaxis: [
    {
      title: {
        text: 'Revenue',
        style: {
          color: '#6B7280',
          fontSize: '14px',
          fontWeight: 700,
        },
      },
      min: 0,
      forceNiceScale: true,
      decimalsInFloat: 0,
      labels: {
        formatter: (value) =>
          value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
            useGrouping: true,
          }),
        style: {
          colors: '#6B7280',
          fontSize: '14px',
          fontWeight: 500,
        },
      },
    },
    {
      title: {
        text: 'Messages',
        style: {
          color: '#6B7280',
          fontSize: '14px',
          fontWeight: 500,
        },
      },
      opposite: true,
      forceNiceScale: true,
      min: 0,
      decimalsInFloat: 0,
      labels: {
        formatter: (value) => value.toLocaleString('en-US', { useGrouping: true }),
        style: {
          colors: '#6B7280',
          fontSize: '14px',
          fontWeight: 500,
        },
      },
    },
  ],
};

export interface MetricsHeaderChartProps {
  metrics: StoreDailyMetricsResponse[] | undefined;
  height: number;
}

export function MetricsHeaderChart({ metrics, height }: MetricsHeaderChartProps) {
  const chartSeries = useMemo(() => {
    if (!metrics) {
      return [];
    }
    return [
      {
        name: 'Attributed Revenue',
        color: '#1A56DB',
        data: metrics.map(({ date, centsDriven }) => ({
          x: date,
          y: centsDriven / 100,
        })),
      },
      {
        name: 'Delivered Messages',
        color: '#FDBA8C',
        data: metrics.map(({ date, sentMessages }) => ({
          x: date,
          y: sentMessages,
        })),
      },
    ];
  }, [metrics]);

  return (
    <Suspense>
      <ReactApexChart
        type='area'
        options={chartOptions}
        series={chartSeries}
        height={height}
        width='100%'
      />
    </Suspense>
  );
}
